import { FormEvent, useState } from "react";

import { placeFacts } from "./placeFacts";
import {
  BLOCK_LABELS,
  ResearchBlock,
  ResearchChoice,
  ResearchDepth,
  ResearchMode,
  STANDARD_TOPICS,
} from "./types";

const valueList = (value: any) => (Array.isArray(value) ? value.filter(Boolean).join(", ") : value);

const toggle = <T,>(values: T[], value: T, on: boolean) =>
  on ? [...values, value] : values.filter((item) => item !== value);

const Checkbox = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label className="flex items-center gap-2">
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    {label}
  </label>
);

export const StartResearchDialog = ({
  parcel,
  onClose,
  onStart,
  busy,
  initial = {},
  title = "Kavelonderzoek starten",
}: {
  parcel: any;
  onClose: () => void;
  onStart: (choice: ResearchChoice) => void;
  busy: boolean;
  initial?: Partial<ResearchChoice>;
  title?: string;
}) => {
  const [mode, setMode] = useState<ResearchMode>(
    initial.mode ?? (initial.question ? "question" : "standard")
  );
  const [question, setQuestion] = useState(initial.question ?? "");
  const [topics, setTopics] = useState<string[]>(
    initial.topics?.length ? initial.topics : STANDARD_TOPICS
  );
  const [blocks, setBlocks] = useState<ResearchBlock[]>(
    initial.blocks ?? (Object.keys(BLOCK_LABELS) as ResearchBlock[])
  );
  const [depth, setDepth] = useState<ResearchDepth>(initial.depth ?? "extensive");
  // De omvang is ook een voorzet voor de losse blokken: beknopt betekent
  // alleen het antwoord, uitgebreid alles. Daarna nog vrij aan te passen.
  const chooseDepth = (next: ResearchDepth) => {
    setDepth(next);
    setBlocks(next === "concise" ? [] : (Object.keys(BLOCK_LABELS) as ResearchBlock[]));
  };
  const data = parcel.data || {};
  const layers = data.data || {};
  const address = data.addresses?.[0];
  const ready = mode === "standard" ? topics.length > 0 : question.trim().length > 0;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onStart({
      mode,
      question: mode === "question" ? question.trim() : "",
      // In de volgorde van de lijst, niet van het aanklikken.
      topics: STANDARD_TOPICS.filter((topic) => topics.includes(topic)),
      blocks,
      depth,
    });
  };

  const facts = [
    ["Oppervlakte", parcel.area ? `${Math.round(parcel.area).toLocaleString("nl-NL")} m²` : null],
    ["Adres", address && `${address.street} ${address.number}, ${address.city}`],
    ...placeFacts(parcel.place),
    ["Bestemming", valueList(layers.zoningSingle?.map((item: any) => item.name))],
    [
      "Dubbelbestemming",
      valueList(layers.zoningDouble?.map((item: any) => item.data?.naam || item.name)),
    ],
    ["Grondgebruik", valueList(layers.landCover?.map((item: any) => item.name))],
    [
      "Bebouwing",
      data.builtUp?.buildings?.length ? `${data.builtUp.buildings.length} pand(en)` : null,
    ],
  ].filter(([, value]) => value);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <form
        onSubmit={submit}
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
      >
        <h2 className="text-lg font-bold">{title}</h2>
        <p className="mt-1 text-sm font-semibold">{parcel.name}</p>
        <dl className="mt-3 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-xs">
          {facts.map(([label, value]) => (
            <div key={label as string} className="contents">
              <dt className="text-gray-500">{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <fieldset className="mt-4 text-xs">
          <legend className="font-semibold">Wat wil je onderzoeken?</legend>
          <div className="mt-1 flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="research-mode"
                checked={mode === "standard"}
                onChange={() => setMode("standard")}
              />
              Standaardonderwerpen
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="research-mode"
                checked={mode === "question"}
                onChange={() => setMode("question")}
              />
              Eigen vraag
            </label>
          </div>
        </fieldset>

        {mode === "standard" ? (
          <fieldset className="mt-3 space-y-1 text-xs">
            <legend className="text-gray-600">
              Elk gekozen onderwerp wordt een hoofdstuk in het rapport.
            </legend>
            {STANDARD_TOPICS.map((topic) => (
              <Checkbox
                key={topic}
                label={topic}
                checked={topics.includes(topic)}
                onChange={(on) => setTopics(toggle(topics, topic, on))}
              />
            ))}
          </fieldset>
        ) : (
          <div className="mt-3 text-xs">
            <label className="block text-gray-600" htmlFor="research-question">
              Alleen deze vraag wordt onderzocht; het model deelt het rapport zelf in.
            </label>
            <textarea
              id="research-question"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
              placeholder="Bijvoorbeeld: is dit perceel geschikt voor woningbouw?"
            />
            <div className="mt-2 flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="research-depth"
                  checked={depth === "concise"}
                  onChange={() => chooseDepth("concise")}
                />
                Beknopt (hoogstens 3 hoofdstukken)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="research-depth"
                  checked={depth === "extensive"}
                  onChange={() => chooseDepth("extensive")}
                />
                Uitgebreid (hoogstens 8)
              </label>
            </div>
          </div>
        )}

        <fieldset className="mt-4 space-y-1 text-xs">
          <legend className="font-semibold">Optioneel in het rapport</legend>
          {(Object.entries(BLOCK_LABELS) as [ResearchBlock, string][]).map(([block, label]) => (
            <Checkbox
              key={block}
              label={label}
              checked={blocks.includes(block)}
              onChange={(on) => setBlocks(toggle(blocks, block, on))}
            />
          ))}
          <p className="text-gray-500">Samenvatting, onzekerheden en bronnen staan er altijd in.</p>
        </fieldset>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded px-3 py-2 text-sm">
            Annuleren
          </button>
          <button
            type="submit"
            disabled={busy || !ready}
            className="rounded bg-bright-yellow px-4 py-2 text-sm font-bold hover:bg-yellow disabled:opacity-50"
          >
            {busy ? "Starten…" : "Start onderzoek"}
          </button>
        </div>
      </form>
    </div>
  );
};
